import { defineStore } from 'pinia';
import {
	type SelectOption,
	CountryUtil,
	type SelectOptionModel,
	Constants,
} from '@fibl/oxs-common';
import * as SearchRepo from '@/repository/search';
import { SelectOptionUtil } from '@fibl/oxs-common';
import { EAV } from '@fibl/zdb-common';

type Ent = EAV.Entity<SelectOption.SelectOption>;

export const useSelectOptionStore = defineStore('selectOption', {
	state: () => ({
		loaded: [] as string[],
		options: [] as Ent[],
	}),

	getters: {
		byId() {
			const idx = new Map(Object.values(this.options).map((e) => [e.id, e]));
			return <T = SelectOptionModel>(id: string): T | undefined => {
				if (!idx.has(id)) return undefined;
				const ent = idx.get(id)!;
				const m = SelectOptionUtil.modelFactory(ent.data.type);
				m.fromEntity(ent);
				return m as any;
			};
		},
		byType() {
			return (type: SelectOption.OptionType): Ent[] => {
				return this.options.filter((e) => e.data.type === type);
			};
		},
		amountUnitsForOffer() {
			return (type: SelectOption.OptionType = 'amountunit'): Ent[] => {
				return this.options.filter(
					(e) =>
						e.data.type === type &&
						e.data.attributes.find(
							(at) => at.key === Constants.ATTRIBUTE_CUSTOM_INPUTS.KEY_FOR_OFFER,
						)?.value,
				);
			};
		},
		amountUnitsForRequest() {
			return (type: SelectOption.OptionType = 'amountunit'): Ent[] => {
				return this.options.filter(
					(e) =>
						e.data.type === type &&
						!e.data.attributes.find(
							(at) => at.key === Constants.ATTRIBUTE_CUSTOM_INPUTS.KEY_FOR_OFFER,
						)?.value,
				);
			};
		},
		byTypeModel() {
			return (type: SelectOption.OptionType): SelectOptionModel[] => {
				return this.byType(type).map((e) => {
					let m = SelectOptionUtil.modelFactory(type);
					m.fromEntity(e);
					return m;
				});
			};
		},
		activeByType() {
			return (type: SelectOption.OptionType): Ent[] => {
				return this.options.filter((e) => e.data.type === type && e.data.active);
			};
		},
		activeByTypeAndCountry() {
			return (type: SelectOption.OptionType, cc: CountryUtil.CountryCode): Ent[] => {
				return this.options.filter(
					(e) =>
						e.data.type === type &&
						e.data.active &&
						(e.data.activeInCountries.includes(cc) ||
							!e.data.activeInCountries?.length),
				);
			};
		},
	},

	actions: {
		async loadAll() {
			await this.loadSpecified();
		},
		async loadByType(type: SelectOption.OptionType) {
			await this.loadSpecified([], [type]);
		},
		async loadForCountry(cc: CountryUtil.CountryCode) {
			await this.loadSpecified([cc]);
		},
		loadSpecified(ccs: CountryUtil.CountryCode[] = [], types: SelectOption.OptionType[] = []) {
			if (!ccs.length) ccs = CountryUtil.OxsMemberList;
			if (!types.length) types = SelectOptionUtil.TypeList;
			let loadCcs = new Set<CountryUtil.CountryCode>();
			let loadTypes = new Set<SelectOption.OptionType>();
			for (let cc of ccs) {
				for (let t of types) {
					let key = `${cc}_${t}`;
					if (this.loaded.includes(key)) continue;
					loadCcs.add(cc);
					loadTypes.add(t);
					this.loaded.push(key);
				}
			}
			if (!loadCcs.size && !loadTypes.size) return Promise.resolve();
			//extracted to keep this method sync
			return this._loadInternal(Array.from(loadCcs), Array.from(loadTypes));
		},
		async _loadInternal(
			loadCcs: CountryUtil.CountryCode[] = [],
			loadTypes: SelectOption.OptionType[] = [],
		) {
			const res: Ent[] = await SearchRepo.findAll(
				'selectoption',
				{
					$and: [
						{
							$or: [
								...loadCcs.map((cc) => ({
									field: '/data/availableForCountries',
									comparison: 'eq',
									value: cc,
								})),
								{
									$not: {
										field: '/data',
										comparison: 'exists',
										value: 'availableForCountries',
									},
								},
							],
						},
						{
							$or: loadTypes.map((t) => ({
								field: '/data/type',
								comparison: 'eq',
								value: t,
							})),
						},
					],
				},
				true,
			);
			let idx = new Set(this.options.map((e) => e.id));
			this.options = this.options.concat(res.filter((e) => !idx.has(e.id)));
		},
	},
});
